import { Component, OnInit, ViewChild, Inject, HostListener, OnDestroy, EventEmitter } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { FileUploader } from 'ng2-file-upload';
import { every, filter } from 'lodash';
import { NewStoryService } from './new.service';
import { FacebookService } from '../../services/facebook.service';
import { AppConfigService } from '../../services/app-config.service';
import { UploadWizardService } from '../wizard/wizard.service';
import { ImagePreprocessingService } from './image-preprocessing.service';
import { ImageFilteringService } from './image-filtering.service';
import { Observable, Subscription } from 'rxjs';
import { FirebaseService } from 'app/services/firebase.service';
import { DetectPlatformService } from '../../services/detectPlatform.service';
import { PhotoGridService } from 'app/services/photo-grid.service';
import { AddPhotosToGridService } from 'app/services/add-photos-to-grid.service';
import { ImageModel } from './_models/image.model';
import { MlImageDeduplicationService } from './ml-image-deduplication.service';
import { FacebookPixelService } from 'app/services/facebook-pixel.service';
import { ProjectService } from 'app/services/project.service';
import { Project } from '../interfaces/project';

@Component({
  selector: 'new-story',
  styleUrls: ['./new.component.scss'],
  templateUrl: './new.component.html',
})
export class NewStoryComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput', { static: false }) private fileInput;

  isSelectionModeShowing;
  isAssistantModalShowing = false;
  assistantModalShowed = false;
  displaySelectedPhotosConfirmationModal = false;
  displayDedupChoiceModal;
  dedupChoiceSelected = false;
  uploader: any = new FileUploader({});
  hasBaseDropZoneOver = false;
  minPhotosCount;
  maxPhotosCount;
  passMinPhotoCheck = false;
  showConfirmLeavePage;
  redirectUrl;
  storyCreationCancelled;
  isChecked = true;
  photoGridShow = true;
  optionButtonsHide = false;
  allSelected = true;
  public shortcut;
  isOk = false;
  dedupIncompatibleBrowser = false;
  addMorePhotos = false;

  subscriptions = new Subscription();

  processedImagesObservable: Observable<ImageModel[]>;
  isPreprocessingImages: Observable<boolean>;
  isWarmingUp: Observable<boolean>;
  processedImages: ImageModel[];

  oldUploaderQueueLength = 0;
  gridShowing = false;



  constructor(
    private router: Router,
    public newStoryService: NewStoryService,
    public fb: FacebookService,
    public uploadWizardService: UploadWizardService,
    private imagePreprocessingService: ImagePreprocessingService,
    private imageFilteringService: ImageFilteringService,
    private appConfigService: AppConfigService,
    private firebaseService: FirebaseService,
    public detectPlatformService: DetectPlatformService,
    public photoGridService: PhotoGridService,
    private addPhotoToGridService: AddPhotosToGridService,
    private mlImageDeduplicationService: MlImageDeduplicationService,
    private facebookPixelService: FacebookPixelService,
    private projectService: ProjectService,
  ) {
    this.processedImagesObservable = this.imagePreprocessingService.images;
    this.isPreprocessingImages = this.imagePreprocessingService.isPreprocessing;
    this.isWarmingUp = this.mlImageDeduplicationService.warmingUp;
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (window.scrollY >= 280) {
      this.addPhotoToGridService.hideAddPhotosHeader(false);
    } else {
      this.addPhotoToGridService.hideAddPhotosHeader(true);
    }
  }

  ngOnInit() {
    this.subscriptions.add(
      this.processedImagesObservable
      .subscribe(images => {
        this.processedImages = images;
      }),
    );
    this.uploadWizardService.cleanWizard();
    this.newStoryService.initUploadStatus();
    this.getShortcut();
    this.minPhotosCount = this.appConfigService.config.min_upload_image_count;
    this.maxPhotosCount = this.appConfigService.config.max_upload_image_count;
    this.facebookPixelService.trackEvent('CustomizeProduct', {});
    this.onDesignModeSubmit();
    this.storyCreationCancelled = false;
    this.addPhotoToGridService.uploader.subscribe(response => {
      if (response) {
        this.selectPhotos();
      }
    });
    this.projectService.create({}).subscribe((project: Project) => {
      this.newStoryService.projectId = project.id;
      this.firebaseService.logEvent('design_request_enter', {
        project_id: this.newStoryService.projectId,
      });
    });

    this.photoGridService.dedupdone.subscribe(response => {
      this.optionButtonsHide = response;
    });

    const userAgent = window.navigator.userAgent;
    if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      this.disableDeduplicationChoice();
    }
  }

  disableDeduplicationChoice() {
    this.deduplication(false);
    this.dedupChoiceSelected = true;
    this.dedupIncompatibleBrowser = true;
  }

  // todo remove if not used
  handleDesignModeSelection() {
    const designModes = this.appConfigService.config.design_modes;
    if (designModes.length > 1) {
      this.isSelectionModeShowing = true;
    } else {
      this.isSelectionModeShowing = false;
      this.uploadWizardService.details.design_mode = designModes[0];
    }
  }

  getShortcut() {
    if (!this.detectPlatformService.isMobilePlatform) {
      if (this.detectPlatformService.getPlatformV2() === 'macos') {
        this.shortcut = '⌘ + a';
      } else {
        this.shortcut = 'CTRL + a';
      }
    }
  }

  onDesignModeSubmit() {
    this.uploadWizardService.details.design_mode = 'automatic';
    if (this.uploadWizardService.details.design_mode === 'automatic') {
      this.uploadWizardService.specs.image_filtering_level = 'all';
    }
    this.isSelectionModeShowing = false;
    this.processedImages.forEach((photo: any) => {
      photo.unchecked = undefined;
    });
  }

  get isMobilePlatform() {
    return !!this.detectPlatformService.isMobilePlatform;
  }

  navigateBack() {
    if (this.processedImages.length) {
      this.showLeavePageModal();
      this.redirectUrl = '/stories';
    } else {
      this.router.navigate(['/stories']);
    }
  }

  showAssistantModal() {
    const displayModal =
      !this.assistantModalShowed;
    if (displayModal) {
      this.isAssistantModalShowing = true;
    } else {
      this.fileInput.nativeElement.click();
    }
  }

  hideAssistantModal() {
    this.isAssistantModalShowing = false;
  }

  selectPhotos() {
    this.assistantModalShowed = true;
    this.hideAssistantModal();
    this.fileInput.nativeElement.click();
  }

  async onPhotoAdded() {

    this.gridShowing = true;

    if (!this.dedupChoiceSelected) {
      this.displayDedupChoiceModal = true;
      this.dedupChoiceSelected = true;
    }

    this.uploader.queue = this.imageFilteringService.filter(
      this.uploader.queue
    );

    this.imagePreprocessingService.preprocess(this.uploader.queue.slice(this.oldUploaderQueueLength));
    this.oldUploaderQueueLength = this.uploader.queue.length;

    this.uploader.queue = this.imageFilteringService.filter(
      this.uploader.queue
    );

    this.firebaseService.logEvent('design_request_images_select', {
      total: this.uploader.queue.length,
    });

    if (!this.addMorePhotos) {
      this.addMorePhotos = true;
    } else {
      this.firebaseService.logEvent('design_request_add_more_photos', {
        project_id: this.newStoryService.projectId,
        image_count: this.uploader.queue.length,
      });
    }
    this.uploader.queue.sort((a, b) => (a.date > b.date) ? 1 : ((b.date > a.date) ? -1 : 0));
  }

  isUploadReady(isPreprocessingImages) {
    if (this.selectedPhotosCount() >= this.minPhotosCount && !this.passMinPhotoCheck) {
      this.firebaseService.logEvent('design_request_min_photos_select', {
        project_id: this.newStoryService.projectId,
      });
      this.facebookPixelService.trackEvent('MinPhotoSelect', {});
      this.passMinPhotoCheck = true;
    }
    return (
      !isPreprocessingImages &&
      this.processedImages.length && !this.isUploadLess() && !this.isUploadMore() &&
      this.selectedPhotosCount() >= this.minPhotosCount && !this.optionButtonsHide
    );
  }

  isUploadLess() {
      return (
        this.uploader &&
        this.processedImages &&
        this.selectedPhotosCount() > 0 &&
        this.selectedPhotosCount() < this.minPhotosCount
      );
  }

  isUploadMore() {
      return (
        this.uploader &&
        this.processedImages &&
        this.selectedPhotosCount() > this.maxPhotosCount
      );
  }

  hideConfirmationModal() {
    this.displaySelectedPhotosConfirmationModal = false;
  }

  onPhotosSubmit() {
    this.displaySelectedPhotosConfirmationModal = true;
    this.upload();
    this.firebaseService.logEvent('design_request_selection_finished', {
      project_id: this.newStoryService.projectId,
      image_count: this.selectedPhotosCount(),
    });
  }

  async deduplication(isDedupEnabled) {
    this.displayDedupChoiceModal = false;
    if (isDedupEnabled) {
      this.mlImageDeduplicationService._warmingUp.next(true);
      this.firebaseService.logEvent('design_request_deduplication_activate', {});
      this.facebookPixelService.trackEvent('DeduplicationOn', {});
    } else {
      this.firebaseService.logEvent('design_request_deduplication_disable', {});
      this.facebookPixelService.trackEvent('DeduplicationOff', {});
    }
    setTimeout(() => {
      if (isDedupEnabled) {
        this.mlImageDeduplicationService.warmUp();
      }
      this.photoGridService.dedupEnabled(isDedupEnabled);
    }, 20);
  }

  checkValue(event: any) {
    this.deduplication(event);
  }

  upload() {
    this.firebaseService.logEvent('design_request_selection_finished', {});
    this.facebookPixelService.trackEvent('SelectionFinished', {});
    window.scrollTo(0, 0);
    this.newStoryService.images = this.processedImages;
    this.addPhotoToGridService.hideAddPhotosHeader(true);
    this.router.navigate(['/stories/upload_wizard']);
  }


  hasAlbums() {
    return this.newStoryService.hasAlbums();
  }

  removePhoto(index) {
    this.processedImages.splice(index, 1);
  }

  togglePhoto(photo) {
    photo.unchecked = !photo.unchecked;
  }


  checkSelectionValue(event: any) {
    if (event) {
      this.isChecked = true;
      this.selectAll(true);
      this.firebaseService.logEvent('design_request_photos_select_all', {
        project_id: this.newStoryService.projectId,
        selected: 1,
        selected_count: this.selectedPhotosCount(),
      });
    } else {
      this.isChecked = false;
      this.selectAll(false);
      this.firebaseService.logEvent('design_request_photos_select_all', {
        project_id: this.newStoryService.projectId,
        selected: 0,
        selected_count: this.selectedPhotosCount(),
      });
    }
 }

 selectAll(option) {
  if (option === true) {
    this.processedImages.forEach((photo: any) => {
      if (!photo.isDuplicate) {
        photo.unchecked = false;
      }
    });
  } else {
    this.processedImages.forEach((photo: any) => {
      photo.unchecked = true;
    });
  }
}


  selectedPhotosCount() {
    return filter(this.processedImages, this.isSelected).length;
  }

  isSelected(photo) {
    return !photo.unchecked;
  }


  uploadHintKey() {
    return true
      ? 'all_photos_will_be_used'
      : 'send_us_all_photos';
  }

  showLeavePageModal() {
    this.showConfirmLeavePage = true;
  }

  confirmLeavingPage() {
    this.showConfirmLeavePage = false;
    this.storyCreationCancelled = true;
    this.router.navigate([this.redirectUrl]);
  }

  stayOnPage() {
    this.redirectUrl = '';
    this.showConfirmLeavePage = false;
  }

  getHeaderClass(isProcessing, isWarmingUp) {
    let tclass: String = 'header';
    if (this.optionButtonsHide || isProcessing || isWarmingUp) {
      tclass += ' while-deduping';
    }
    return tclass;
  }

  canDeactivate(nextState: RouterStateSnapshot): Observable<boolean> | boolean {
    if (
      nextState.url !== '/stories/upload_wizard' &&
      nextState.url !== '/stories/new' &&
      this.processedImages.length &&
      !this.storyCreationCancelled
    ) {
      this.showLeavePageModal();
      this.redirectUrl = nextState.url;
      return false;
    }
    return true;
  }

  ngOnDestroy(): void {
    this.addPhotoToGridService.hideAddPhotosHeader(true);
    this.uploader.queue = 0;
    this.imagePreprocessingService.clear();
    this.subscriptions.unsubscribe();
  }
}
